import { useEffect, useState } from "react";
import moment from "moment/moment";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import Box from "@mui/material/Box";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ErrorIcon from "@mui/icons-material/Error";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { getLocale } from "react-i18nify";
import { Typography } from "@mui/material";
import { convertIntToCurrency } from "src/common";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useDialog } from "src/context/DialogContext";
import FilterDialog from "src/components/purchase/FilterDialog";
import Stack from "@mui/material/Stack";
import Main from "src/components/core/Main";
import {
  getShippingOrderCsv,
  getShippingOrderList,
} from "src/api/shippingOrder";
import SearchInput from "src/components/purchase/SearchInput";
import FilterMobileButton from "src/components/purchase/FilterMobileButton";
import ExportCsvButton from "src/components/purchase/ExportCsvButton";
import DateSelected from "src/components/purchase/DateSelected";
import SellerSelected from "src/components/purchase/SellerSelected";
import StatusBar from "src/components/purchase/StatusBar";
import {
  Container,
  WrapperFilter,
  WrapperDataGrid,
  WrapperStatusRefund,
  WrapperMobileButton,
} from "../../component";
import NoResultsOverlay from "../../NoResultsOverlay";
import NoRowsOverlay from "../../NoRowsOverlay";
import { useAuth } from "src/hooks/useAuth";
import useFilterOrder from "../../hooks/useFilterOrder";

const ShippingList = ({ open }) => {
  const { user } = useAuth();

  const [formatDataCsv, setFormatDataCsv] = useState([]);
  const { openDialog, closeDialog } = useDialog();
  const isSmallerThanTablet = useMediaQuery("(max-width:767px)");
  const isMobile = useMediaQuery("(max-width:425px)");

  const isAdmin = user?.role_id === 1;

  const config = {
    module: "shipping",
    initialState: {
      keyword: "",
      page: 0,
      page_size: 10,
      status: "",
      date: null,
      seller: "",
      sortBy: "order.orderNumber",
      sortOrder: "desc",
    },
    initialVisibilityModel: {
      deliveryDate: !isSmallerThanTablet,
      sellerName: true,
      orderNumber: true,
      customerName: !isSmallerThanTablet,
      status: true,
      price: !isSmallerThanTablet,
      delivery: !isSmallerThanTablet,
      actions: true,
    },
    onFetch: getShippingOrderList,
    onFetchCsv: getShippingOrderCsv,
    isSmallerThanTablet,
    dependencies: [
      "page",
      "date",
      "seller",
      "status",
      "sortBy",
      "sortOrder",
      "carrier",
    ],
  };

  const {
    dataList,
    dataCsv,
    onSearchButton,
    handlePageChange,
    rowCount,
    statusCount,
    dataSearch,
    setDataSearch,
    isLoading,
    isLoadingCsv,
    columnVisibilityModel,
    handleFilterDialog,
  } = useFilterOrder(config);

  const columns = [
    {
      field: "orderNumber",
      type: "number",
      flex: 1.5,
      minWidth: isSmallerThanTablet ? 92 : 125,
      editable: false,
      sortable: true,
      headerAlign: "left",
      align: "left",
      disableColumnMenu: true,
      renderHeader: () => (
        <Stack direction="column">
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: isSmallerThanTablet ? "0.75rem" : "0.875rem",
            }}
          >
            {isMobile ? "Order No." : "Order Number"}
          </Typography>
          <Typography
            sx={{
              fontWeight: 400,
              color: "#6C6C70",
              fontSize: isSmallerThanTablet ? "0.625rem" : "0.75rem",
            }}
          >
            {isMobile ? "QT No." : "QT Number"}
          </Typography>
        </Stack>
      ),
      renderCell: (params) => {
        return (
          <div style={{ overflow: "hidden" }}>
            <div
              className="rowitem"
              style={{
                fontWeight: 400,
                color: "#252525 ",
                fontSize: isSmallerThanTablet ? "0.75rem" : "0.875rem",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {params?.row?.orderNumber}
            </div>
            <div
              className="rowitem"
              style={{
                fontWeight: 400,
                color: "#6C6C70",
                fontSize: "0.75rem",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {params?.row?.qtNumber}
            </div>
          </div>
        );
      },
    },
    {
      field: "sellerName",
      flex: 2,
      minWidth: isSmallerThanTablet ? 120 : 130,
      editable: false,
      sortable: false,
      headerAlign: "left",
      align: "left",
      disableColumnMenu: true,
      renderHeader: () => (
        <Stack direction="column">
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: isSmallerThanTablet ? "0.75rem" : "0.875rem",
            }}
          >
            ร้านค้า
          </Typography>
          <Typography
            sx={{
              fontWeight: 400,
              color: "#6C6C70",
              fontSize: isSmallerThanTablet ? "0.625rem" : "0.75rem",
            }}
          >
            {isSmallerThanTablet ? "วันที่คาดว่าจัดส่ง" : "สินค้า"}
          </Typography>
        </Stack>
      ),
      renderCell: (params) => {
        return (
          <Stack
            direction="column"
            sx={{
              padding: "24px 0px",
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
          >
            <Typography
              fontSize={isSmallerThanTablet ? "0.75rem" : "0.875rem"}
              className="rowitem"
              sx={{
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
              }}
            >
              {params?.row?.sellerName ? params?.row?.sellerName : "-"}
            </Typography>
            {isSmallerThanTablet ? (
              <Typography
                sx={{
                  fontSize: "0.625rem",
                  color: "#6C6C70",
                }}
              >
                {params?.row?.scheduleDate
                  ? moment(params?.row?.scheduleDate).format("DD-MM-YYYY")
                  : "-"}
              </Typography>
            ) : (
              params?.row?.productChoiceOrders?.map(
                ({ productChoiceOrder }) => (
                  <Box
                    key={productChoiceOrder?._id}
                    sx={{
                      display: "flex",
                      gap: "8px",
                      color: "#6C6C70",
                      paddingLeft: "4px",
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Box style={{ fontSize: "0.75rem", fontWeight: "400" }}>
                      •
                    </Box>
                    <Box
                      style={{
                        fontSize: "0.75rem",
                        fontWeight: "400",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {`${productChoiceOrder?.product?.item_name} ${productChoiceOrder?.color?.color_th ? productChoiceOrder?.color?.color_th : ""}`}
                    </Box>
                  </Box>
                ),
              )
            )}
          </Stack>
        );
      },
    },
    {
      field: "customerName",
      flex: 1,
      minWidth: 160,
      editable: false,
      sortable: false,
      headerAlign: "left",
      align: "left",
      disableColumnMenu: true,
      renderHeader: () => (
        <Stack direction="column">
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 500 }}>
            ชื่อลูกค้า
          </Typography>
          <Typography
            sx={{ fontSize: "0.75rem", fontWeight: 400, color: "#6C6C70" }}
          >
            เบอร์ติดต่อ
          </Typography>
        </Stack>
      ),
      renderCell: (params) => {
        return (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "start",
              outline: "none",
              overflow: "hidden",
              textOverflow: "ellipsis",
              padding: "24px 0px",
            }}
          >
            <div style={{ width: "100%" }}>
              <div
                className="MuiDataGrid-cellContent"
                style={{
                  fontSize: "0.875rem",
                  fontWeight: 400,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {params.row?.shippingAddress?.name
                  ? `${params.row?.shippingAddress?.name}`
                  : `-`}
              </div>
              <div
                className="MuiDataGrid-cellContent"
                style={{
                  fontSize: "0.75rem",
                  fontWeight: 400,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  color: "#6C6C70",
                }}
              >
                {params.row?.shippingAddress?.tel
                  ? `${params.row?.shippingAddress?.tel}`
                  : `-`}
              </div>
            </div>
          </Box>
        );
      },
    },
    {
      field: "deliveryDate",
      type: "number",
      flex: 1,
      minWidth: 150,
      editable: false,
      sortable: true,
      headerAlign: "left",
      align: "left",
      disableColumnMenu: true,
      renderHeader: () => (
        <Stack direction="column">
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 500 }}>
            วันที่คาดว่าจัดส่ง
          </Typography>
          <Typography
            sx={{ fontSize: "0.75rem", fontWeight: 400, color: "#6C6C70" }}
          >
            วันที่จัดส่งจริง
          </Typography>
        </Stack>
      ),
      renderCell: (params) => {
        // index 0 mean seller to express or seller to customer
        // index 1 mean express to customer
        const { fulfillmentProcess = [] } = params?.row || {};

        const shippedDate =
          fulfillmentProcess.length > 1
            ? fulfillmentProcess[1]?.shippedDate
            : fulfillmentProcess[0]?.shippedDate;
        return (
          <Stack
            direction="column"
            sx={{
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
          >
            <Typography
              fontSize="0.875rem"
              className="rowitem"
              sx={{
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
              }}
            >
              {params.row.scheduleDate
                ? moment(params.row.scheduleDate).format("DD-MM-YYYY")
                : "-"}
            </Typography>

            <Typography
              fontSize="0.875rem"
              className="rowitem"
              sx={{
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
                color: "#6C6C70",
                fontSize: "0.75rem",
              }}
            >
              {shippedDate ? moment(shippedDate).format("DD-MM-YYYY") : "-"}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: "delivery",
      flex: 1,
      minWidth: 120,
      editable: false,
      sortable: false,
      headerAlign: "left",
      align: "left",
      disableColumnMenu: true,
      headerName: getLocale() === "th" ? "ขนส่ง" : "delivery",
      renderCell: (params) => {
        // index 0 mean seller to express or seller to customer
        // index 1 mean express to customer
        const { fulfillmentProcess = [] } = params?.row || {};
        const carrier =
          fulfillmentProcess.length > 1
            ? fulfillmentProcess[1]?.carrier
            : fulfillmentProcess[0]?.carrier || "-";
        return <div className="rowitem">{carrier}</div>;
      },
    },
    {
      field: "price",
      flex: 0.5,
      minWidth: 85,
      editable: false,
      sortable: false,
      headerAlign: "center",
      align: "center",
      disableColumnMenu: true,
      headerName: getLocale() === "th" ? "ราคาสินค้า" : "price",
      renderCell: (params) => {
        return (
          <div className="rowitem">
            {convertIntToCurrency(params?.row?.totalAll)}
          </div>
        );
      },
    },
    {
      field: "status",
      headerClassName: "status-header",
      flex: 1,
      minWidth: isSmallerThanTablet ? 48 : 150,
      editable: false,
      sortable: false,
      headerAlign: isSmallerThanTablet ? "center" : "left",
      align: isSmallerThanTablet ? "center" : "left",
      flexDirection: "column",
      disableColumnMenu: true,
      headerName: getLocale() === "th" ? "สถานะ" : "Status",
      renderCell: (params) => {
        return (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              // margin: "8px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: isSmallerThanTablet ? "center" : "start",
              outline: "none",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <Stack
              direction="row"
              alignItems={"center"}
              justifyContent={"start"}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: handleStatusColor(params.row.status),
                  marginRight: 1,
                }}
              />

              {!isSmallerThanTablet && (
                <Typography
                  sx={{
                    fontSize: "0.875rem",
                    maxWidth: "100px",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                  }}
                >
                  {handleStatusWord(params.row.status)}
                </Typography>
              )}
            </Stack>

            {params.row.status_refund === "refund" ? (
              <WrapperStatusRefund>
                <span
                  style={{
                    color: "#E13B30",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                  }}
                >
                  ออเดอร์มีการคืนเงิน
                </span>
              </WrapperStatusRefund>
            ) : null}

            {params.row.status_refund === "wait refund" ? (
              <WrapperStatusRefund>
                <ErrorIcon
                  sx={{
                    color: "#E13B30",
                  }}
                />

                <span
                  style={{
                    color: "#E13B30",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                  }}
                >
                  ออเดอร์รอการคืนเงิน
                </span>
              </WrapperStatusRefund>
            ) : null}

            {params.row.status_refund === "succ refund" ? (
              <WrapperStatusRefund>
                <CheckCircleIcon
                  sx={{
                    color: "#3CB11E",
                  }}
                />

                <span
                  style={{
                    color: "#6A665A",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                  }}
                >
                  ออเดอร์คืนเงินสำเร็จ
                </span>
              </WrapperStatusRefund>
            ) : null}
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "ดำเนินการ",
      type: "actions",
      flex: 1,
      minWidth: 40,
      sortable: false,
      disableColumnMenu: true,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<VisibilityIcon />}
          label="รายละเอียด"
          onClick={() =>
            window.open(`/shipping/detail/${params.row.shipping_id}`, "_blank")
          }
          showInMenu
        />,
      ],
    },
  ];

  useEffect(() => {
    if (dataCsv.length > 0) {
      const format = dataCsv.map((item, index) => {
        const {
          createdAt,
          sellerName,
          orderNumber,
          refPayNo,
          productChoiceOrders,
          totalAll,
          scheduleDate,
          qtNumber,
          shippingAddress,
          statusPayment,
          statusPaymentRePay,
        } = item.order;

        const { status } = item;

        const { fulfillmentProcess = [] } = item;

        const isTwoFulfillments = fulfillmentProcess.length > 1;

        const shippedDate = isTwoFulfillments
          ? fulfillmentProcess[1]?.shippedDate
          : fulfillmentProcess[0]?.shippedDate;

        const estimatedDeliveryDate = isTwoFulfillments
          ? fulfillmentProcess[1]?.estimatedDeliveryDate
          : fulfillmentProcess[0]?.estimatedDeliveryDate;

        const carrier = isTwoFulfillments
          ? fulfillmentProcess[1]?.carrier
          : fulfillmentProcess[0]?.carrier || "-";
        return {
          ลำดับที่: index + 1,
          วันที่สั่งซื้อ: moment(createdAt).format("DD-MM-YYYY"),
          หมายเลขออเดอร์: orderNumber ? orderNumber : "-",
          qtNumber: qtNumber ? qtNumber : "-",
          ชื่อร้านค้า: sellerName ? sellerName : "-",
          ราคา: totalAll ? convertIntToCurrency(totalAll) : "-",
          สถานะ: status.value ? handleStatusWord(status.value) : "-",
          จำนวนสินค้า: productChoiceOrders.length,
          หมายเลขอ้างอิง: refPayNo ? refPayNo : "-",
          รายการสินค้า: productChoiceOrders.map(
            (item) => `•${item?.productChoiceOrder?.product?.item_name}\n`,
          ),
          ชื่อลูกค้า: shippingAddress?.name ? shippingAddress?.name : "-",
          ที่อยู่: shippingAddress?.address ? shippingAddress?.address : "-",
          จังหวัด: shippingAddress?.province ? shippingAddress?.province : "-",
          อำเภอ: shippingAddress?.district ? shippingAddress?.district : "-",
          ตำบล: shippingAddress?.subdistrict
            ? shippingAddress?.subdistrict
            : "-",
          เลขไปรษณีย์: shippingAddress?.zipcode
            ? shippingAddress?.zipcode
            : "-",
          เบอร์ติดต่อ: shippingAddress?.tel ? shippingAddress?.tel : "-",
          วันที่คาดว่าจะส่ง: scheduleDate
            ? moment(scheduleDate).format("DD-MM-YYYY")
            : "-",
          วันที่จัดส่งแล้ว: shippedDate
            ? moment(shippedDate).format("DD-MM-YYYY")
            : "-",
          วันที่คาดว่าจะถึง: estimatedDeliveryDate
            ? moment(estimatedDeliveryDate).format("DD-MM-YYYY")
            : "-",
          ขนส่ง: carrier,
          ใบเสร็จที่1: statusPayment?.paySlipImage
            ? statusPayment?.paySlipImage
            : "-",
          ใบเสร็จที่2: statusPaymentRePay?.paySlipOtherImage
            ? statusPaymentRePay?.paySlipOtherImage
            : "-",
        };
      });

      setFormatDataCsv(format);
    } else {
      setFormatDataCsv([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataCsv]);

  const allStatusCount = Object.values(statusCount).reduce(
    (acc, cur) => acc + cur,
    0,
  );

  const handleStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "#FBB634";
      case "confirmed":
        return "#52A8E7";
      case "preparing":
        return "#9A66ED";
      case "delivering":
        return "#EE7608";
      case "shipped":
        return "#f54287";
      case "delivered":
        return "#219653";
      case "failed":
        return "#BF0013";
      default:
        return "rgba(255,255,255, 0);";
    }
  };

  const statusTabs = [
    { label: "ทั้งหมด", count: allStatusCount, color: "", value: "" },
    {
      label: "รอจัดส่ง",
      count: statusCount?.pending || 0,
      color: handleStatusColor("pending"),
      value: "pending",
    },
    {
      label: "ยืนยันการจัดส่ง",
      count: statusCount?.confirmed || 0,
      color: handleStatusColor("confirmed"),
      value: "confirmed",
    },
    {
      label: "เตรียมจัดส่ง",
      count: statusCount?.preparing || 0,
      color: handleStatusColor("preparing"),
      value: "preparing",
    },
    {
      label: "กำลังส่ง",
      count: statusCount?.delivering || 0,
      color: handleStatusColor("delivering"),
      value: "delivering",
    },
    {
      label: "รอยืนยันรับสินค้า",
      count: statusCount?.shipped || 0,
      color: handleStatusColor("shipped"),
      value: "shipped",
    },
    {
      label: "ส่งสำเร็จ",
      count: statusCount?.delivered || 0,
      color: handleStatusColor("delivered"),
      value: "delivered",
    },
    {
      label: "ส่งไม่สำเร็จ",
      count: statusCount?.failed || 0,
      color: handleStatusColor("failed"),
      value: "failed",
    },
  ];

  const handleStatusWord = (status) => {
    return statusTabs.find((item) => item?.value === status)?.label;
  };

  const handleOpenDialog = () => {
    openDialog({
      type: "custom",
      customDialog: (
        <FilterDialog
          isFilterSeller={true}
          dataSearch={dataSearch}
          onClose={() => closeDialog()}
          onConfirm={handleFilterDialog}
          dateLabel="วันที่คาดว่าจัดส่ง"
        />
      ),
    });
  };

  const handleColumnClick = ({ field }) => {
    if (field === "orderNumber" || field === "deliveryDate") {
      setDataSearch((prev) => ({
        ...prev,
        sortBy:
          field === "orderNumber" ? "order.orderNumber" : "order.scheduleDate",
        sortOrder: dataSearch.sortOrder === "asc" ? "desc" : "asc",
        page: 0,
      }));
    }
  };

  if (!isAdmin) return;

  return (
    <Main open={open}>
      <Container>
        <WrapperFilter>
          <SearchInput
            onSearch={onSearchButton}
            setDataSearch={setDataSearch}
          />

          <WrapperMobileButton isSmallerThanTablet={isSmallerThanTablet}>
            <FilterMobileButton
              onDialog={handleOpenDialog}
              dataSearch={dataSearch}
            />

            <ExportCsvButton
              dataList={dataCsv}
              formatDataCsv={formatDataCsv}
              isSmallerThanTablet={isSmallerThanTablet}
              topic="รายการจัดส่งสินค้า"
              isLoading={isLoadingCsv}
              mobile
            />
          </WrapperMobileButton>

          <DateSelected
            isSmallerThanTablet={isSmallerThanTablet}
            dateLabel={"วันที่คาดว่าจัดส่ง"}
            dataSearch={dataSearch}
            setDataSearch={setDataSearch}
          />

          <SellerSelected
            isSmallerThanTablet={isSmallerThanTablet}
            dataSearch={dataSearch}
            setDataSearch={setDataSearch}
          />

          <ExportCsvButton
            dataList={dataCsv}
            formatDataCsv={formatDataCsv}
            isSmallerThanTablet={isSmallerThanTablet}
            topic="รายการจัดส่งสินค้า"
            isLoading={isLoadingCsv}
          />
        </WrapperFilter>

        <StatusBar
          dataSearch={dataSearch}
          setDataSearch={setDataSearch}
          statusTabs={statusTabs}
          isSmallerThanTablet={isSmallerThanTablet}
        />

        <WrapperDataGrid>
          <Box
            sx={{
              width: "100%",
              height: "100%",
              overflowX: "scroll",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": {
                display: "none",
              },
            }}
          >
            <DataGrid
              rows={dataList}
              getRowId={(row) => row._id}
              getRowHeight={() => "auto"}
              loading={isLoading}
              columns={columns}
              pageSize={10}
              rowCount={rowCount}
              paginationMode="server"
              page={dataSearch.page}
              onPageChange={(e) => handlePageChange(e)}
              rowsPerPageOptions={[10]}
              pagination
              disableSelectionOnClick
              components={{
                NoRowsOverlay,
                NoResultsOverlay,
              }}
              componentsProps={{
                basePopper: {},
              }}
              sx={{
                "& .MuiDataGrid-actionsCell": {
                  transform: "rotate(90deg)",
                },
                "&. MuiDataGrid-columnHeaderTitleContainerContent": {
                  lineHeight: "normal",
                },
                "& .MuiDataGrid-columnHeader": {
                  fontSize: isSmallerThanTablet ? "0.75rem" : "0.875rem",
                },
                "& .status-header": {
                  padding: 0,
                },
              }}
              disableColumnResize={false}
              columnVisibilityModel={columnVisibilityModel}
              onColumnHeaderClick={handleColumnClick}
            />
          </Box>
        </WrapperDataGrid>
      </Container>
    </Main>
  );
};

export default ShippingList;
